import { z } from 'zod';

/**
 * Lifecycle of a booking. Stored as free text in the column (VarChar), so a
 * row written before a status existed still reads back fine — this enum is
 * the contract for everything that WRITES a status (workflow booking node,
 * PATCH /bookings/:id) and the list the UI offers.
 */
export const bookingStatusSchema = z.enum(['active', 'cancelled', 'rescheduled']);

export type BookingStatus = z.infer<typeof bookingStatusSchema>;

export const BOOKING_STATUSES = bookingStatusSchema.options;

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  active: 'Active',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
};

export const bookingSchema = z.object({
  id: z.string().uuid(),
  date: z.string(),
  title: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  status: z.string().default('active'),
  attendance: z.enum(['attended', 'not_attended']).nullable().optional(),
  has_time: z.boolean().default(true),
  call_id: z.string().uuid().nullable().optional(),
  /** Structured payload written at creation (booking(add) dataMappings) or edited in the UI. */
  data: z.record(z.string(), z.unknown()).nullable().optional(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type Booking = z.infer<typeof bookingSchema>;

export const bookingWithRelationsSchema = bookingSchema.extend({
  call: z
    .object({
      id: z.string().uuid(),
      contact: z
        .object({
          id: z.string().uuid(),
          name: z.string().nullable(),
          phone: z.string().nullable(),
        })
        .nullable()
        .optional(),
      campaign: z
        .object({
          id: z.string().uuid(),
          name: z.string(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
});

export type BookingWithRelations = z.infer<typeof bookingWithRelationsSchema>;

export const createBookingSchema = z.object({
  date: z.string().min(1),
  title: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  has_time: z.boolean().optional(),
  call_id: z.string().uuid().nullable().optional(),
  data: z.record(z.string(), z.unknown()).nullable().optional(),
});

export type CreateBookingDto = z.infer<typeof createBookingSchema>;

export const updateBookingSchema = createBookingSchema.partial().extend({
  status: bookingStatusSchema.optional(),
});

export type UpdateBookingDto = z.infer<typeof updateBookingSchema>;

export const bookingQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).default(20),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  campaignId: z.string().uuid().optional(),
  callId: z.string().uuid().optional(),
  search: z.string().optional(),
  status: z.string().optional(),
  sortBy: z.enum(['date', 'created_at', 'title']).default('date'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

export type BookingQuery = z.infer<typeof bookingQuerySchema>;
